import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { funcionarioFranquiaService } from "../../services/funcionarioFranquiaService";
import { MembroFranquiaAPI, CargoFranquia } from "../../types/Funcionario";
import { useAuth } from "../../contexts/AuthContext";
import { ConditionalRender } from "../../components/ProtectedRoute";
import { GerenciarCargos } from "../../components/GerenciarCargos";
import { Permission } from "../../types/auth";

export const DetalhesFuncionario: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [membro, setMembro] = useState<MembroFranquiaAPI | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmBlock, setShowConfirmBlock] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showGerenciarCargos, setShowGerenciarCargos] = useState(false);
  const [cargosLoading, setCargosLoading] = useState(false);

  const franquiaId = user?.franquia_membership?.franquia_id;
  const isGestor = hasRole("GESTOR");
  const viewedUserIsAdmin = membro?.cargos.includes("ADMIN");

  useEffect(() => {
    const fetchMembro = async () => {
      if (!franquiaId || !id) {
        setError("Parâmetros inválidos");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await funcionarioFranquiaService.obterMembro(
          franquiaId,
          parseInt(id)
        );
        setMembro(response);
        setError("");
      } catch (error: any) {
        console.error("Erro ao buscar detalhes do membro:", error);
        setError("Erro ao carregar detalhes do funcionário");
      } finally {
        setLoading(false);
      }
    };

    fetchMembro();
  }, [franquiaId, id]);

  const handleToggleStatus = async () => {
    if (!membro || !franquiaId) return;

    try {
      setActionLoading(true);
      const bloquear = !membro.user.is_bloqueado;

      await funcionarioFranquiaService.alterarStatusMembro(
        franquiaId,
        membro.id_membro_franquia,
        bloquear
      );

      // Atualizar os dados do membro
      setMembro((prev) =>
        prev
          ? {
              ...prev,
              user: {
                ...prev.user,
                is_bloqueado: bloquear,
              },
            }
          : null
      );

      setShowConfirmBlock(false);
    } catch (error: any) {
      console.error("Erro ao alterar status:", error);
      alert("Erro ao alterar status do funcionário");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExcluirMembro = async () => {
    if (!membro || !franquiaId) return;

    try {
      setActionLoading(true);

      await funcionarioFranquiaService.excluirMembro(
        franquiaId,
        membro.id_membro_franquia
      );

      // Redirecionar para lista após exclusão
      navigate("/funcionarios");
    } catch (error: any) {
      console.error("Erro ao excluir membro:", error);
      alert("Erro ao excluir funcionário");
      setActionLoading(false);
    }
  };

  const handleAtualizarCargos = async (novosCargos: CargoFranquia[]) => {
    if (!membro || !franquiaId) return;

    try {
      setCargosLoading(true);

      const membroAtualizado = await funcionarioFranquiaService.atualizarCargos(
        franquiaId,
        membro.id_membro_franquia,
        novosCargos
      );

      // Atualizar os dados do membro
      setMembro(membroAtualizado);
      setShowGerenciarCargos(false);
    } catch (error: any) {
      console.error("Erro ao atualizar cargos:", error);
      throw error; // Re-throw para o componente GerenciarCargos tratar
    } finally {
      setCargosLoading(false);
    }
  };

  const formatCargos = (cargos: CargoFranquia[]): string => {
    const cargoLabels: Record<CargoFranquia, string> = {
      ADMIN: "Administrador",
      GESTOR: "Gestor",
      FINANCEIRO: "Financeiro",
      CADASTRAMENTO: "Cadastramento",
      VENDEDOR: "Vendedor",
    };

    return cargos.map((cargo) => cargoLabels[cargo] || cargo).join(", ");
  };

  const getStatusBadge = (membro: MembroFranquiaAPI) => {
    if (!membro.user.conta_ativa) {
      return (
        <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-800 rounded-full">
          Conta Inativa
        </span>
      );
    }

    if (membro.user.is_bloqueado) {
      return (
        <span className="px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
          Bloqueado
        </span>
      );
    }

    if (membro.status_na_franquia === "ATIVO") {
      return (
        <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
          Ativo
        </span>
      );
    }

    return (
      <span className="px-3 py-1 text-sm font-medium bg-yellow-100 text-yellow-800 rounded-full">
        {membro.status_na_franquia}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !membro) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">
            {error || "Funcionário não encontrado"}
          </p>
          <button
            onClick={() => navigate("/funcionarios")}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Voltar para lista de funcionários
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Detalhes do Funcionário
          </h1>
          <p className="text-gray-600 mt-1">
            Informações completas do membro da franquia
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/funcionarios"
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Voltar
          </Link>
        </div>
      </div>

      {/* Informações do Funcionário */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados Pessoais */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Dados Pessoais
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nome Completo
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {membro.user.nome_completo}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <p className="mt-1 text-sm text-gray-900">{membro.user.email}</p>
            </div>
            {membro.user.cpf && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  CPF
                </label>
                <p className="mt-1 text-sm text-gray-900">{membro.user.cpf}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status da Conta
              </label>
              <div className="mt-1 flex items-center justify-between">
                <div>{getStatusBadge(membro)}</div>
                <ConditionalRender permission={Permission.MANAGE_USERS}>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowConfirmBlock(true)}
                      disabled={actionLoading || (isGestor && viewedUserIsAdmin)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${
                        membro.user.is_bloqueado
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-orange-600 text-white hover:bg-orange-700"
                      } disabled:opacity-50`}
                    >
                      {actionLoading ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      ) : membro.user.is_bloqueado ? (
                        "Desbloquear"
                      ) : (
                        "Bloquear"
                      )}
                    </button>
                    <button
                      onClick={() => setShowConfirmDelete(true)}
                      disabled={actionLoading || (isGestor && viewedUserIsAdmin)}
                      className="px-3 py-1 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      Excluir
                    </button>
                  </div>
                </ConditionalRender>
              </div>
            </div>
          </div>
        </div>

        {/* Informações da Franquia */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informações na Franquia
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cargos
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {formatCargos(membro.cargos)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status na Franquia
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {membro.status_na_franquia}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Data de Ingresso
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(membro.created).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Última Modificação
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(membro.modified).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Informações de Sistema */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informações do Sistema
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Data de Criação
              </label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(membro.user.created).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
            {membro.user.last_login ? (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Último Login
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(membro.user.last_login).toLocaleDateString(
                    "pt-BR",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Último Login
                </label>
                <p className="mt-1 text-sm text-gray-500 italic">
                  Nunca fez login
                </p>
              </div>
            )}
            {membro.excluido_em && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Data de Exclusão
                </label>
                <p className="mt-1 text-sm text-red-600">
                  {new Date(membro.excluido_em).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Permissões e Cargos Detalhados */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Cargos e Permissões
            </h2>
            <ConditionalRender permission={Permission.MANAGE_USERS}>
              <button
                onClick={() => setShowGerenciarCargos(true)}
                disabled={actionLoading || (isGestor && viewedUserIsAdmin)}
                className="bg-blue-600 text-white px-3 py-1 text-sm rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Gerenciar Cargos
              </button>
            </ConditionalRender>
          </div>
          <div className="space-y-3">
            {membro.cargos.map((cargo) => (
              <div
                key={cargo}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <h3 className="font-medium text-gray-900">
                    {cargo === "ADMIN" && "Administrador"}
                    {cargo === "GESTOR" && "Gestor"}
                    {cargo === "FINANCEIRO" && "Financeiro"}
                    {cargo === "CADASTRAMENTO" && "Cadastramento"}
                    {cargo === "VENDEDOR" && "Vendedor"}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {cargo === "ADMIN" && "Acesso total ao sistema"}
                    {cargo === "GESTOR" &&
                      "Gerenciamento de funcionários e operações"}
                    {cargo === "FINANCEIRO" && "Gestão financeira e relatórios"}
                    {cargo === "CADASTRAMENTO" &&
                      "Cadastro de imóveis e clientes"}
                    {cargo === "VENDEDOR" && "Vendas e atendimento ao cliente"}
                  </p>
                </div>
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  {cargo}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de Confirmação - Bloquear/Desbloquear */}
      {showConfirmBlock && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                {membro.user.is_bloqueado ? "Desbloquear" : "Bloquear"}{" "}
                Funcionário
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Tem certeza que deseja{" "}
                  {membro.user.is_bloqueado ? "desbloquear" : "bloquear"} o
                  funcionário <strong>{membro.user.nome_completo}</strong>?
                </p>
                {!membro.user.is_bloqueado && (
                  <p className="text-sm text-red-600 mt-2">
                    ⚠️ O funcionário não conseguirá mais acessar o sistema.
                  </p>
                )}
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowConfirmBlock(false)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleToggleStatus}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-md transition-colors disabled:opacity-50 ${
                      membro.user.is_bloqueado
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-orange-600 text-white hover:bg-orange-700"
                    }`}
                  >
                    {actionLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processando...
                      </div>
                    ) : (
                      `Confirmar ${
                        membro.user.is_bloqueado ? "Desbloqueio" : "Bloqueio"
                      }`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Excluir */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Excluir Membro da Franquia
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Tem certeza que deseja excluir{" "}
                  <strong>{membro.user.nome_completo}</strong> da franquia?
                </p>
                <p className="text-sm text-red-600 mt-2">
                  ⚠️ Esta ação marcará o membro como INATIVO e ele perderá
                  acesso à franquia.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  O usuário poderá ser reativado posteriormente por um
                  administrador.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowConfirmDelete(false)}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleExcluirMembro}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Excluindo...
                      </div>
                    ) : (
                      "Confirmar Exclusão"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gerenciar Cargos */}
      {showGerenciarCargos && (
        <GerenciarCargos
          cargosAtuais={membro.cargos}
          onSalvar={handleAtualizarCargos}
          onCancelar={() => setShowGerenciarCargos(false)}
          loading={cargosLoading}
        />
      )}
    </div>
  );
};